import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { logout } from '../../action/Auth';
import { Menu, LogIn, LogOut, FileEdit, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '../context/ThemeContext';
import { socket } from '../context/socket';
import { getStatistics } from '../../action/Statistics';
import Store from '../../Store';

const Navbar = ({ auth, statistics, logout, bugerClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [currentPage, setCurrentPage] = useState('');

  useEffect(() => {
    const pageNames = {
      '/login': 'Đăng Nhập',
      '/register': 'Đăng Ký',
      '/dashboard': 'Thống Kê',
      '/orders': 'Đơn Hàng',
      '/chat': 'Hội Thoại',
      '/reports': 'Báo Cáo',
      '/products': 'Sản Phẩm',
      '/doisoat': 'Đối Soát',
      '/setting': 'Cấu Hình',
      '/admin': 'Admin',
      '/': 'Home',
    };
    if (location.pathname.startsWith('/chat')) {
      setCurrentPage('Hội Thoại');
    } else {
      setCurrentPage(pageNames[location.pathname] || '');
    }
  }, [location]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.partition) return;

    // Join the user partition room to receive socket updates
    socket.emit('joinRoom', { username: auth.user._id, room: auth.user.partition });

    const onNewOrder = () => {
      Store.dispatch(getStatistics());
    };

    const onChangeOrderStatus = () => {
      Store.dispatch(getStatistics());
    };

    socket.on('newOrder', onNewOrder);
    socket.on('changeOrderStatus', onChangeOrderStatus);

    return () => {
      socket.off('newOrder', onNewOrder);
      socket.off('changeOrderStatus', onChangeOrderStatus);
    };
  }, [auth.isAuthenticated, auth.user?.partition]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isChatPage = location.pathname.startsWith('/chat');

  return (
    <header className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30 pt-[env(safe-area-inset-top,0px)] shadow-[0_1px_3px_0_rgb(0_0_0/0.03)] dark:shadow-[0_1px_3px_0_rgb(0_0_0/0.2)]">
      <div className="flex items-center justify-between h-14 px-3 sm:px-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={bugerClick}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-semibold text-slate-800 dark:text-slate-200 text-xs md:text-sm uppercase tracking-wide">
            {currentPage}
          </span>
        </div>

        {isChatPage && statistics && (
          <div className="hidden md:flex items-center gap-6 px-4 py-1.5 bg-slate-50/50 dark:bg-slate-800/40 rounded-full border border-slate-100 dark:border-slate-800/60 text-xs text-slate-600 dark:text-slate-350 shadow-inner-soft select-none">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-slate-400 dark:text-slate-500">Đơn hôm nay:</span>
              <span className="font-bold text-slate-800 dark:text-slate-200">{statistics.homnay?.sodon || 0}</span>
            </div>
            <div className="h-3 w-px bg-slate-200 dark:bg-slate-800" />
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-slate-400 dark:text-slate-500">Doanh thu:</span>
              <span className="font-bold text-blue-600 dark:text-blue-400">{(statistics.homnay?.doanhthu || 0).toLocaleString('vi-VN')}đ</span>
            </div>
            <div className="h-3 w-px bg-slate-200 dark:bg-slate-800" />
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-slate-400 dark:text-slate-500">Lợi nhuận:</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">{(statistics.homnay?.loinhuan || 0).toLocaleString('vi-VN')}đ</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {!auth.loading && (
            auth.isAuthenticated ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-medium"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                Logout
              </Button>
            ) : (
              <div className="flex items-center gap-0.5 md:gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/register')}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs"
                >
                  <FileEdit className="h-4 w-4 mr-1" />
                  Register
                </Button>
                <span className="text-slate-300 dark:text-slate-600">|</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 text-xs"
                >
                  <LogIn className="h-4 w-4 mr-1" />
                  Login
                </Button>
              </div>
            )
          )}
        </div>
      </div>
    </header>
  );
};

Navbar.propTypes = {
  auth: PropTypes.object.isRequired,
  statistics: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  bugerClick: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.AuthRedux,
  statistics: state.Statistics,
});

export default connect(mapStateToProps, { logout })(Navbar);
